// src/admin/pages/FinanceReports.js
import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../firebase';
import { collection, query, getDocs, where, orderBy, Timestamp } from 'firebase/firestore';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import AdminSidebar from '../../components/AdminSidebar';
import AdminHeader from '../../components/AdminHeader';
import * as XLSX from 'xlsx';
import { exportToPDFArabic } from '../../utils/pdfExporter';
import '../../styles/FinanceReports.css';

const FinanceReports = () => {
  // حالة البيانات
  const [transactions, setTransactions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // حالة الفلاتر
  const [dateRange, setDateRange] = useState('month');
  const [startDate, setStartDate] = useState(getStartDateFromRange('month'));
  const [endDate, setEndDate] = useState(new Date());
  const [selectedRestaurant, setSelectedRestaurant] = useState('all');
  const [selectedDriver, setSelectedDriver] = useState('all');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('all');
  const [selectedTransactionType, setSelectedTransactionType] = useState('all');
  const [reportType, setReportType] = useState('overview'); // 'overview', 'restaurant', 'driver', 'payments'
  
  // بيانات القوائم المنسدلة
  const [restaurants, setRestaurants] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [paymentMethods] = useState([]);
  
  // بيانات المخططات والإحصاءات
  const [revenueChartData, setRevenueChartData] = useState([]);
  const [paymentMethodChartData, setPaymentMethodChartData] = useState([]);
  const [restaurantRevenueData, setRestaurantRevenueData] = useState([]);
  const [driverRevenueData, setDriverRevenueData] = useState([]);
  const [restaurantBalanceData, setRestaurantBalanceData] = useState([]);
  
  // إحصاءات مالية
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalOrdersAmount: 0,
    totalDeliveryFees: 0,
    totalRestaurantDeposits: 0,
    totalRestaurantWithdrawals: 0,
    totalCashPayments: 0,
    totalCardPayments: 0,
    totalKnetPayments: 0,
    avgOrderValue: 0,
    avgDeliveryFee: 0
  });
  
  // الدوال المساعدة
  const formatDate = useCallback((date) => {
    if (!date) return '';
    const options = { day: 'numeric', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('ar-EG', options);
  }, []);

  const formatCurrency = useCallback((amount) => {
    if (amount === undefined || amount === null) return '0.000 د.ك';
    return `${Number(amount).toFixed(3)} د.ك`;
  }, []);

  // دالة تحويل التاريخ من Firestore
  const toDateTime = useCallback((timestamp) => {
    if (!timestamp) return null;
    if (timestamp instanceof Date) return timestamp;
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    return new Date(timestamp);
  }, []);

  // دالة الحصول على تاريخ البداية من النطاق
  function getStartDateFromRange(range) {
    const now = new Date();
    const startDate = new Date();
    
    switch (range) {
      case 'today':
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'yesterday':
        startDate.setDate(now.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        startDate.setMonth(now.getMonth() - 3);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        startDate.setMonth(now.getMonth() - 1);
    }
    
    return startDate;
  }

  // دالة حساب الإحصائيات المالية
  const calculateFinancialStats = useCallback((ordersData, transactionsData) => {
    const stats = {
      totalRevenue: 0,
      totalOrdersAmount: 0,
      totalDeliveryFees: 0,
      totalRestaurantDeposits: 0,
      totalRestaurantWithdrawals: 0,
      totalCashPayments: 0,
      totalCardPayments: 0,
      totalKnetPayments: 0,
      avgOrderValue: 0,
      avgDeliveryFee: 0
    };

    // حساب إحصائيات الطلبات
    ordersData.forEach(order => {
      stats.totalOrdersAmount += order.amount || 0;
      stats.totalDeliveryFees += order.deliveryFee || 0;
      
      // حساب طرق الدفع
      if (order.paymentMethod === 'نقدي') {
        stats.totalCashPayments += order.totalAmount || 0;
      } else if (order.paymentMethod === 'بطاقة') {
        stats.totalCardPayments += order.totalAmount || 0;
      } else if (order.paymentMethod === 'كي نت') {
        stats.totalKnetPayments += order.totalAmount || 0;
      }
    });

    // حساب إحصائيات المعاملات
    transactionsData.forEach(transaction => {
      if (transaction.type === 'deposit') {
        stats.totalRestaurantDeposits += transaction.amount || 0;
      } else if (transaction.type === 'withdrawal') {
        stats.totalRestaurantWithdrawals += transaction.amount || 0;
      }
    });

    // حساب المتوسطات
    stats.totalRevenue = stats.totalOrdersAmount + stats.totalDeliveryFees;
    stats.avgOrderValue = ordersData.length > 0 ? stats.totalOrdersAmount / ordersData.length : 0;
    stats.avgDeliveryFee = ordersData.length > 0 ? stats.totalDeliveryFees / ordersData.length : 0;

    setStats(stats);
  }, []);

  // دالة إعداد بيانات مخطط الإيرادات
  const prepareRevenueChartData = useCallback((ordersData) => {
    const map = {};
    const today = new Date();
    
    // إنشاء نقاط البيانات للفترة المحددة
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      map[formatDate(d)] = { date: formatDate(d), revenue: 0, orders: 0 };
    }
    
    // ملء البيانات
    ordersData.forEach(order => {
      const date = order.createdAtFormatted;
      if (map[date]) {
        map[date].revenue += order.totalAmount || 0;
        map[date].orders += 1;
      }
    });
    
    setRevenueChartData(Object.values(map));
  }, [formatDate]);

  // دالة إعداد بيانات مخطط طرق الدفع
  const preparePaymentMethodChartData = useCallback((ordersData) => {
    const map = {};
    
    ordersData.forEach(order => {
      const method = order.paymentMethod || 'غير معروف';
      if (!map[method]) {
        map[method] = { name: method, value: 0 };
      }
      map[method].value += order.totalAmount || 0;
    });
    
    setPaymentMethodChartData(Object.values(map));
  }, []);

  // دالة إعداد بيانات إيرادات المطاعم
  const prepareRestaurantRevenueData = useCallback((ordersData) => {
    const map = {};
    
    ordersData.forEach(order => {
      const restaurant = order.restaurantName || 'غير معروف';
      if (!map[restaurant]) {
        map[restaurant] = { name: restaurant, revenue: 0, orders: 0 };
      }
      map[restaurant].revenue += order.totalAmount || 0;
      map[restaurant].orders += 1;
    });
    
    setRestaurantRevenueData(Object.values(map).sort((a, b) => b.revenue - a.revenue));
  }, []);

  // دالة إعداد بيانات إيرادات السائقين
  const prepareDriverRevenueData = useCallback((ordersData) => {
    const map = {};
    
    ordersData.forEach(order => {
      const driver = order.driverName || 'غير معروف';
      if (!map[driver]) {
        map[driver] = { name: driver, revenue: 0, orders: 0 };
      }
      map[driver].revenue += order.deliveryFee || 0;
      map[driver].orders += 1;
    });
    
    setDriverRevenueData(Object.values(map).sort((a, b) => b.revenue - a.revenue));
  }, []);

  // دالة إعداد بيانات رصيد المطاعم
  const prepareRestaurantBalanceData = useCallback((transactionsData) => {
    const map = {};
    
    transactionsData.forEach(transaction => {
      const restaurant = transaction.restaurantName || 'غير معروف';
      if (!map[restaurant]) {
        map[restaurant] = { name: restaurant, balance: 0 };
      }
      if (transaction.type === 'deposit') {
        map[restaurant].balance += transaction.amount || 0;
      } else if (transaction.type === 'withdrawal') {
        map[restaurant].balance -= transaction.amount || 0;
      }
    });
    
    setRestaurantBalanceData(Object.values(map).sort((a, b) => b.balance - a.balance));
  }, []);

  // تحميل البيانات من Firestore
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // جلب المطاعم
      const restaurantsSnapshot = await getDocs(collection(db, 'restaurants'));
      const restaurantsData = restaurantsSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || 'مطعم غير معروف',
        balance: doc.data().balance || 0
      }));
      setRestaurants(restaurantsData);
      
      // جلب السائقين
      const driversSnapshot = await getDocs(collection(db, 'drivers'));
      const driversData = driversSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || 'سائق غير معروف'
      }));
      setDrivers(driversData);
      
      // تحويل التواريخ إلى Timestamp
      const firestoreStartDate = Timestamp.fromDate(startDate);
      const firestoreEndDate = Timestamp.fromDate(endDate);
      
      // جلب الطلبات
      let ordersQuery = collection(db, 'orders');
      let ordersConstraints = [
        where('createdAt', '>=', firestoreStartDate),
        where('createdAt', '<=', firestoreEndDate),
        orderBy('createdAt', 'desc')
      ];
      
      // إضافة الفلاتر الإضافية للطلبات
      if (selectedRestaurant !== 'all') {
        ordersConstraints.push(where('restaurantId', '==', selectedRestaurant));
      }
      
      if (selectedDriver !== 'all') {
        ordersConstraints.push(where('driverId', '==', selectedDriver));
      }
      
      if (selectedPaymentMethod !== 'all') {
        ordersConstraints.push(where('paymentMethod', '==', selectedPaymentMethod));
      }
      
      const ordersSnapshot = await getDocs(query(ordersQuery, ...ordersConstraints));
      
      // معالجة بيانات الطلبات
      const ordersData = ordersSnapshot.docs.map(doc => {
        const data = doc.data();
        const createdAt = toDateTime(data.createdAt);
        
        // حساب المبالغ
        let amount = 0;
        if (data.amount !== undefined) {
          amount = parseFloat(data.amount) || 0;
        }
        
        let deliveryFee = 0;
        if (data.deliveryFee !== undefined) {
          deliveryFee = parseFloat(data.deliveryFee) || 0;
        }
        
        return {
          id: doc.id,
          ...data,
          createdAtDate: createdAt,
          createdAtFormatted: createdAt ? formatDate(createdAt) : 'غير معروف',
          amount: amount,
          deliveryFee: deliveryFee,
          totalAmount: amount + deliveryFee,
          status: data.status || 'غير معروف',
          paymentMethod: data.paymentMethod || 'غير معروف'
        };
      });
      
      setOrders(ordersData);
      
      // جلب المعاملات المالية
      let transactionsQuery = collection(db, 'transactions');
      let transactionsConstraints = [
        where('timestamp', '>=', firestoreStartDate),
        where('timestamp', '<=', firestoreEndDate),
        orderBy('timestamp', 'desc')
      ];
      
      // إضافة الفلاتر الإضافية للمعاملات
      if (selectedRestaurant !== 'all') {
        transactionsConstraints.push(where('restaurantId', '==', selectedRestaurant));
      }
      
      if (selectedDriver !== 'all') {
        transactionsConstraints.push(where('driverId', '==', selectedDriver));
      }
      
      if (selectedTransactionType !== 'all') {
        transactionsConstraints.push(where('type', '==', selectedTransactionType));
      }
      
      const transactionsSnapshot = await getDocs(query(transactionsQuery, ...transactionsConstraints));
      
      // معالجة بيانات المعاملات
      const transactionsData = transactionsSnapshot.docs.map(doc => {
        const data = doc.data();
        const timestamp = toDateTime(data.timestamp);
        
        return {
          id: doc.id,
          ...data,
          timestampDate: timestamp,
          timestampFormatted: timestamp ? formatDate(timestamp) : 'غير معروف',
          amount: parseFloat(data.amount) || 0
        };
      });
      
      setTransactions(transactionsData);
      
      // تحديث الإحصائيات والرسوم البيانية
      calculateFinancialStats(ordersData, transactionsData);
      prepareRevenueChartData(ordersData);
      preparePaymentMethodChartData(ordersData);
      prepareRestaurantRevenueData(ordersData);
      prepareDriverRevenueData(ordersData);
      prepareRestaurantBalanceData(transactionsData);
      
    } catch (error) {
      console.error('خطأ في جلب البيانات:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [
    startDate,
    endDate,
    selectedRestaurant,
    selectedDriver,
    selectedPaymentMethod,
    selectedTransactionType,
    toDateTime,
    formatDate,
    calculateFinancialStats,
    prepareRevenueChartData,
    preparePaymentMethodChartData,
    prepareRestaurantRevenueData,
    prepareDriverRevenueData,
    prepareRestaurantBalanceData
  ]);

// تطبيق الفلاتر عند تغيير نطاق التاريخ
useEffect(() => {
  if (dateRange !== 'custom') {
    setStartDate(getStartDateFromRange(dateRange));
    setEndDate(new Date());
  }
}, [dateRange]);

// تحميل البيانات عند تغيير الفلاتر
useEffect(() => {
  fetchData();
}, [fetchData]);

// تصدير البيانات إلى Excel
const exportToExcel = () => {
  try {
    let data = [];
    let filename = '';
    
    if (reportType === 'restaurant') {
      // تصدير بيانات المطاعم
      data = restaurantRevenueData.map(restaurant => ({
        'المطعم': restaurant.name,
        'إجمالي الطلبات': restaurant.ordersCount,
        'الطلبات المكتملة': restaurant.completedCount,
        'الطلبات الملغاة': restaurant.cancelledCount,
        'الإيرادات': restaurant.revenue.toFixed(3),
        'رسوم التوصيل': restaurant.deliveryFees.toFixed(3),
        'المدفوعات النقدية': restaurant.cashPayments.toFixed(3),
        'مدفوعات البطاقات': restaurant.cardPayments.toFixed(3),
        'مدفوعات كي نت': restaurant.knetPayments.toFixed(3),
      }));
      filename = `تقرير_إيرادات_المطاعم_${formatDate(startDate)}_${formatDate(endDate)}.xlsx`;
    } else if (reportType === 'driver') {
      // تصدير بيانات السائقين
      data = driverRevenueData.map(driver => ({
        'السائق': driver.name,
        'إجمالي الطلبات': driver.ordersCount,
        'الطلبات المكتملة': driver.completedCount,
        'الطلبات الملغاة': driver.cancelledCount,
        'رسوم التوصيل': driver.deliveryFees.toFixed(3),
        'المبالغ النقدية المحصلة': driver.cashCollected.toFixed(3),
        'مدفوعات البطاقات': driver.cardPayments.toFixed(3),
        'مدفوعات كي نت': driver.knetPayments.toFixed(3),
        'إجمالي المحصل': driver.totalCollected.toFixed(3)
      }));
      filename = `تقرير_إيرادات_السائقين_${formatDate(startDate)}_${formatDate(endDate)}.xlsx`;
    } else if (reportType === 'payments') {
      // تصدير بيانات المدفوعات
      data = paymentMethodChartData.map(payment => ({
        'طريقة الدفع': payment.name,
        'عدد الطلبات': payment.count,
        'قيمة المدفوعات': payment.revenue.toFixed(3),
        'النسبة المئوية': (payment.count / completedOrders.length * 100).toFixed(2) + '%'
      }));
      filename = `تقرير_طرق_الدفع_${formatDate(startDate)}_${formatDate(endDate)}.xlsx`;
    } else {
      // تصدير البيانات المالية للمعاملات
      data = transactions.map(tx => ({
        'التاريخ': tx.dateFormatted,
        'المصدر': tx.sourceName,
        'نوع المعاملة': tx.type,
        'المبلغ': tx.amount.toFixed(3),
        'الوصف': tx.description || '',
        'رقم الطلب': tx.orderId || '',
      }));
      filename = `تقرير_المعاملات_المالية_${formatDate(startDate)}_${formatDate(endDate)}.xlsx`;
    }
    
    // إنشاء ورقة العمل
    const worksheet = XLSX.utils.json_to_sheet(data);
    
    // إنشاء الكتاب
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'البيانات المالية');
    
    // تصدير الملف
    XLSX.writeFile(workbook, filename);
    
  } catch (error) {
    console.error('خطأ في تصدير ملف Excel:', error);
    setError('حدث خطأ أثناء تصدير البيانات إلى Excel.');
  }
};

// تصدير البيانات إلى PDF
const exportToPDF = () => {
  try {
    // تحضير البيانات للتقرير
    const reportData = {
      title: `التقرير المالي - ${reportTypeTitles[reportType]}`,
      dateRange: `${formatDate(startDate)} - ${formatDate(endDate)}`,
      stats,
      filters: {
        restaurant: selectedRestaurant !== 'all' ? 
          restaurants.find(r => r.id === selectedRestaurant)?.name || 'غير معروف' : 'جميع المطاعم',
        driver: selectedDriver !== 'all' ? 
          drivers.find(d => d.id === selectedDriver)?.name || 'غير معروف' : 'جميع السائقين',
        paymentMethod: selectedPaymentMethod !== 'all' ? selectedPaymentMethod : 'جميع طرق الدفع',
        transactionType: selectedTransactionType !== 'all' ? selectedTransactionType : 'جميع أنواع المعاملات'
      }
    };
    
    // إضافة البيانات الخاصة بالتقرير المحدد
    if (reportType === 'restaurant') {
      reportData.restaurants = restaurantRevenueData.slice(0, 20); // أخذ أول 20 مطعم
    } else if (reportType === 'driver') {
      reportData.drivers = driverRevenueData.slice(0, 20); // أخذ أول 20 سائق
    } else if (reportType === 'payments') {
      reportData.payments = paymentMethodChartData;
    } else {
      reportData.transactions = transactions.slice(0, 50); // أخذ أول 50 معاملة
    }
    
    // استدعاء وظيفة تصدير PDF مع دعم اللغة العربية
    exportToPDFArabic({
      filename: `التقرير_المالي_${reportType}_${formatDate(startDate)}_${formatDate(endDate)}.pdf`,
      margins: { top: 20, right: 20, bottom: 20, left: 20 }
    }, reportData);
    
  } catch (error) {
    console.error('خطأ في تصدير ملف PDF:', error);
    setError('حدث خطأ أثناء تصدير البيانات إلى PDF.');
  }
};

// عناوين التقارير
const reportTypeTitles = {
  overview: 'نظرة عامة',
  restaurant: 'إيرادات المطاعم',
  driver: 'إيرادات السائقين',
  payments: 'طرق الدفع',
  balance: 'أرصدة المطاعم'
};

// الطلبات المكتملة
const completedOrders = orders.filter(order => 
  order.status === 'تم التسليم ✅' || order.status?.includes('تم التسليم')
);

// ألوان المخططات
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#FF6B6B', '#4CAF50', '#FF1493', '#9370DB', '#20B2AA'];

return (
    <React.Fragment>
  <div className="finance-reports-page" dir="rtl">
    <AdminSidebar />
    <div className="finance-reports-content">
      <AdminHeader title="التقارير المالية" />
      {/* فلاتر التقرير */}
      <div className="finance-report-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>نوع التقرير:</label>
            <div className="report-type-tabs">
              <button 
                className={`report-tab ${reportType === 'overview' ? 'active' : ''}`}
                onClick={() => setReportType('overview')}
              >
                نظرة عامة
              </button>
              <button 
                className={`report-tab ${reportType === 'restaurant' ? 'active' : ''}`}
                onClick={() => setReportType('restaurant')}
              >
                المطاعم
              </button>
              <button 
                className={`report-tab ${reportType === 'driver' ? 'active' : ''}`}
                onClick={() => setReportType('driver')}
              >
                السائقين
              </button>
              <button 
                className={`report-tab ${reportType === 'payments' ? 'active' : ''}`}
                onClick={() => setReportType('payments')}
              >
                طرق الدفع
              </button>
              <button 
                className={`report-tab ${reportType === 'balance' ? 'active' : ''}`}
                onClick={() => setReportType('balance')}
              >
                أرصدة المطاعم
              </button>
            </div>
          </div>
        </div>
        
        <div className="filter-row">
          <div className="filter-group">
            <label>النطاق الزمني:</label>
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
              <option value="today">اليوم</option>
              <option value="yesterday">الأمس</option>
              <option value="week">آخر 7 أيام</option>
              <option value="month">آخر 30 يوم</option>
              <option value="quarter">آخر 3 أشهر</option>
              <option value="year">آخر سنة</option>
              <option value="custom">مخصص</option>
            </select>
          </div>
          
          {dateRange === 'custom' && (
            <>
              <div className="filter-group">
                <label>من:</label>
                <input
                  type="date"
                  value={startDate.toISOString().split('T')[0]}
                  onChange={(e) => setStartDate(new Date(e.target.value))}
                />
              </div>
              <div className="filter-group">
                <label>إلى:</label>
                <input
                  type="date"
                  value={endDate.toISOString().split('T')[0]}
                  onChange={(e) => setEndDate(new Date(e.target.value))}
                />
              </div>
            </>
          )}
          
          <div className="filter-group">
            <label>المطعم:</label>
            <select 
              value={selectedRestaurant} 
              onChange={(e) => setSelectedRestaurant(e.target.value)}
              disabled={reportType === 'driver'}
            >
              <option value="all">جميع المطاعم</option>
              {restaurants.map(restaurant => (
                <option key={restaurant.id} value={restaurant.id}>{restaurant.name}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>السائق:</label>
            <select 
              value={selectedDriver} 
              onChange={(e) => setSelectedDriver(e.target.value)}
              disabled={reportType === 'restaurant' || reportType === 'balance'}
            >
              <option value="all">جميع السائقين</option>
              {drivers.map(driver => (
                <option key={driver.id} value={driver.id}>{driver.name}</option>
              ))}
            </select>
          </div>
          
          {reportType !== 'balance' && (
            <div className="filter-group">
              <label>طريقة الدفع:</label>
              <select 
                value={selectedPaymentMethod} 
                onChange={(e) => setSelectedPaymentMethod(e.target.value)}
              >
                <option value="all">جميع طرق الدفع</option>
                {paymentMethods.map(method => (
                  <option key={method.id} value={method.id}>{method.name}</option>
                ))}
              </select>
            </div>
          )}
          
          {reportType === 'overview' && (
            <div className="filter-group">
              <label>نوع المعاملة:</label>
              <select value={selectedTransactionType} onChange={(e) => setSelectedTransactionType(e.target.value)}>
                <option value="all">جميع المعاملات</option>
                <option value="شحن">شحن</option>
                <option value="سحب">سحب</option>
                <option value="خصم">خصم</option>
              </select>
            </div>
          )}
        </div>
        
        <div className="report-actions">
          <button onClick={exportToExcel} className="export-btn excel">
            <i className="fas fa-file-excel"></i>
            تصدير Excel
          </button>
          <button onClick={exportToPDF} className="export-btn pdf">
            <i className="fas fa-file-pdf"></i>
            تصدير PDF
          </button>
        </div>
      </div>
      
      {loading ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>جاري تحميل البيانات...</p>
        </div>
      ) : error ? (
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
        </div>
      ) : (
            <React.Fragment>
          {reportType === 'overview' && (
            <div className="overview-report">
              {/* مخطط تطور الإيرادات */}
              <div className="chart-card">
                <h3>تطور الإيرادات</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={revenueChartData} margin={{ top: 20, right: 30, left: 20, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip formatter={(value, name) => [
                      name === 'revenue' ? formatCurrency(value) : 
                      name === 'deliveryFees' ? formatCurrency(value) : 
                      name === 'orders' ? `${value} طلب` : value,
                      name === 'revenue' ? 'الإيرادات' : 
                      name === 'deliveryFees' ? 'رسوم التوصيل' : 
                      name === 'orders' ? 'عدد الطلبات' : name
                    ]} />
                    <Legend />
                    <Line type="monotone" dataKey="revenue" name="الإيرادات" stroke="#8884d8" strokeWidth={2} activeDot={{ r: 8 }} />
                    <Line type="monotone" dataKey="deliveryFees" name="رسوم التوصيل" stroke="#82ca9d" strokeWidth={2} activeDot={{ r: 8 }} />
                    <Line type="monotone" dataKey="orders" name="عدد الطلبات" stroke="#ffc658" strokeWidth={2} activeDot={{ r: 8 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              
              {/* المعاملات المالية */}
              <div className="transactions-section">
                <h3>المعاملات المالية الأخيرة</h3>
                <div className="transactions-table-container">
                  <table className="transactions-table">
                    <thead>
                      <tr>
                        <th>التاريخ</th>
                        <th>المصدر</th>
                        <th>النوع</th>
                        <th>المبلغ</th>
                        <th>الوصف</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.slice(0, 10).map(tx => (
                        <tr key={tx.id}>
                          <td>{tx.dateFormatted}</td>
                          <td>{tx.sourceName}</td>
                          <td>
                            <span className={`transaction-type ${
                              tx.type === 'شحن' || tx.type === 'deposit' || tx.type === 'إيداع' ? 'deposit' :
                              tx.type === 'سحب' || tx.type === 'withdrawal' ? 'withdrawal' : 'fee'
                            }`}>
                              {tx.type}
                            </span>
                          </td>
                          <td className="amount">
                            {formatCurrency(tx.amount)}
                          </td>
                          <td>{tx.description || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          
          {reportType === 'restaurant' && (
            <div className="restaurant-finance-report">
              {/* مخطط مقارنة إيرادات المطاعم */}
              <div className="chart-card">
                <h3>أفضل المطاعم من حيث الإيرادات</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={restaurantRevenueData.slice(0, 10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={150} />
                    <Tooltip formatter={(value, name) => [
                      name === 'revenue' ? formatCurrency(value) : 
                      name === 'deliveryFees' ? formatCurrency(value) : value,
                      name === 'revenue' ? 'الإيرادات' : 
                      name === 'deliveryFees' ? 'رسوم التوصيل' : name
                    ]} />
                    <Legend />
                    <Bar dataKey="revenue" name="الإيرادات" fill="#8884d8" />
                    <Bar dataKey="deliveryFees" name="رسوم التوصيل" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              {/* جدول إيرادات المطاعم */}
              <div className="restaurant-table-container">
                <h3>تفاصيل إيرادات المطاعم</h3>
                <table className="finance-table">
                  <thead>
                    <tr>
                      <th>المطعم</th>
                      <th>إجمالي الطلبات</th>
                      <th>الطلبات المكتملة</th>
                      <th>الطلبات الملغاة</th>
                      <th>الإيرادات</th>
                      <th>رسوم التوصيل</th>
                      <th>نقدي</th>
                      <th>بطاقة</th>
                      <th>كي نت</th>
                    </tr>
                  </thead>
                  <tbody>
                    {restaurantRevenueData.map(restaurant => (
                      <tr key={restaurant.id}>
                        <td>{restaurant.name}</td>
                        <td>{restaurant.ordersCount}</td>
                        <td>{restaurant.completedCount}</td>
                        <td>{restaurant.cancelledCount}</td>
                        <td>{formatCurrency(restaurant.revenue)}</td>
                        <td>{formatCurrency(restaurant.deliveryFees)}</td>
                        <td>{formatCurrency(restaurant.cashPayments)}</td>
                        <td>{formatCurrency(restaurant.cardPayments)}</td>
                        <td>{formatCurrency(restaurant.knetPayments)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {reportType === 'driver' && (
            <div className="driver-finance-report">
              <div className="chart-card">
                <h3>أفضل السائقين من حيث المبالغ المحصلة</h3>
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={driverRevenueData.slice(0, 10)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={150} />
                      <Tooltip formatter={(value, name) => [
                        name === 'cashCollected' ? formatCurrency(value) : 
                        name === 'deliveryFees' ? formatCurrency(value) : value,
                        name === 'cashCollected' ? 'المبالغ النقدية المحصلة' : 
                        name === 'deliveryFees' ? 'رسوم التوصيل' : name
                      ]} />
                      <Legend />
                      <Bar dataKey="cashCollected" name="المبالغ النقدية المحصلة" fill="#ffc658" />
                      <Bar dataKey="deliveryFees" name="رسوم التوصيل" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="driver-table-container">
                  <h3>تفاصيل إيرادات السائقين</h3>
                  <table className="finance-table">
                    <thead>
                      <tr>
                        <th>السائق</th>
                        <th>إجمالي الطلبات</th>
                        <th>الطلبات المكتملة</th>
                        <th>رسوم التوصيل</th>
                        <th>المبالغ النقدية المحصلة</th>
                        <th>مدفوعات البطاقات</th>
                        <th>مدفوعات كي نت</th>
                        <th>إجمالي المبالغ المحصلة</th>
                      </tr>
                    </thead>
                    <tbody>
                      {driverRevenueData.map(driver => (
                        <tr key={driver.id}>
                          <td>{driver.name}</td>
                          <td>{driver.ordersCount}</td>
                          <td>{driver.completedCount}</td>
                          <td>{formatCurrency(driver.deliveryFees)}</td>
                          <td>{formatCurrency(driver.cashCollected)}</td>
                          <td>{formatCurrency(driver.cardPayments)}</td>
                          <td>{formatCurrency(driver.knetPayments)}</td>
                          <td>{formatCurrency(driver.totalCollected)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {reportType === 'payments' && (
              <div className="payment-methods-report">
                <div className="chart-grid">
                  <div className="chart-card">
                    <h3>توزيع المدفوعات حسب طريقة الدفع</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={paymentMethodChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {paymentMethodChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name, props) => [
                          formatCurrency(value),
                          props.payload.name
                        ]} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  
                  <div className="chart-card">
                    <h3>المدفوعات حسب طريقة الدفع</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={paymentMethodChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip formatter={(value, name) => [
                          name === 'revenue' ? formatCurrency(value) : 
                          name === 'count' ? `${value} طلب` : value,
                          name === 'revenue' ? 'المبلغ' : 
                          name === 'count' ? 'عدد الطلبات' : name
                        ]} />
                        <Legend />
                        <Bar dataKey="revenue" name="المبلغ" fill="#8884d8" />
                        <Bar dataKey="count" name="عدد الطلبات" fill="#82ca9d" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                <div className="payment-methods-table-container">
                  <h3>تفاصيل طرق الدفع</h3>
                  <table className="finance-table">
                    <thead>
                      <tr>
                        <th>طريقة الدفع</th>
                        <th>عدد الطلبات</th>
                        <th>النسبة المئوية</th>
                        <th>إجمالي المبالغ</th>
                        <th>متوسط قيمة الطلب</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentMethodChartData.map(payment => (
                        <tr key={payment.name}>
                          <td>{payment.name}</td>
                          <td>{payment.count}</td>
                          <td>{completedOrders.length > 0 ? (payment.count / completedOrders.length * 100).toFixed(2) : 0}%</td>
                          <td>{formatCurrency(payment.revenue)}</td>
                          <td>{payment.count > 0 ? formatCurrency(payment.revenue / payment.count) : formatCurrency(0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {reportType === 'balance' && (
              <div className="restaurant-balance-report">
                <div className="chart-card">
                  <h3>أرصدة المطاعم</h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={restaurantBalanceData.slice(0, 15)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={150} />
                      <Tooltip formatter={(value, name) => [
                        formatCurrency(value),
                        name === 'balance' ? 'الرصيد الحالي' : 
                        name === 'deposits' ? 'إجمالي الإيداعات' : 
                        name === 'withdrawals' ? 'إجمالي السحوبات' : 
                        name === 'spentOnDelivery' ? 'رسوم التوصيل المدفوعة' : name
                      ]} />
                      <Legend />
                      <Bar dataKey="balance" name="الرصيد الحالي" fill="#8884d8" />
                      <Bar dataKey="deposits" name="إجمالي الإيداعات" fill="#4CAF50" />
                      <Bar dataKey="withdrawals" name="إجمالي السحوبات" fill="#FF5722" />
                      <Bar dataKey="spentOnDelivery" name="رسوم التوصيل المدفوعة" fill="#2196F3" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="restaurant-balance-table-container">
                  <h3>تفاصيل أرصدة المطاعم</h3>
                  <table className="finance-table">
                    <thead>
                      <tr>
                        <th>المطعم</th>
                        <th>الرصيد الحالي</th>
                        <th>إجمالي الإيداعات</th>
                        <th>إجمالي السحوبات</th>
                        <th>رسوم التوصيل المدفوعة</th>
                        <th>عدد المعاملات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {restaurantBalanceData.map(restaurant => (
                        <tr key={restaurant.id} className={restaurant.balance < 0 ? 'negative-balance' : ''}>
                          <td>{restaurant.name}</td>
                          <td className={restaurant.balance < 0 ? 'negative-amount' : 'positive-amount'}>
                            {formatCurrency(restaurant.balance)}
                          </td>
                          <td>{formatCurrency(restaurant.deposits)}</td>
                          <td>{formatCurrency(restaurant.withdrawals)}</td>
                          <td>{formatCurrency(restaurant.spentOnDelivery)}</td>
                          <td>{restaurant.transactions.length}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
            </React.Fragment>
            )}
          </div>
      </div>
    </React.Fragment>
  );
};

export default FinanceReports;